import type { ReactNode } from 'react';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { useScrollReset } from '../../hooks/useScrollReset';
import { Skeleton } from './Skeleton';
import { StateBlock } from './StateBlock';
import './ui.css';

const SKELETON_ROWS = 6;

export interface DataTableColumn<T> {
  key: string;
  header: string;
  numeric?: boolean;
  render: (row: T) => ReactNode;
}

export interface DataTableSelection<T, K> {
  selected: ReadonlySet<K>;
  onToggle: (key: K) => void;
  onToggleAll: () => void;
  /** Names the row's checkbox. Without it every box reads "Select row", which
   *  is useless on a page whose selection feeds a bulk delete. */
  rowLabel: (row: T) => string;
}

interface DataTableProps<T, K extends string | number> {
  /** Describes the table for screen readers; rendered visually hidden. */
  caption: string;
  columns: readonly DataTableColumn<T>[];
  rows: readonly T[];
  rowKey: (row: T) => K;
  status: 'loading' | 'ready' | 'error';
  /**
   * A refetch behind rows that are already on screen (new search term, next
   * page, manual refresh). The rows stay, so the panel neither collapses nor
   * flashes; only a progress line and `aria-busy` mark the wait.
   */
  refreshing?: boolean;
  error?: string | null;
  onRetry?: () => void;
  emptyTitle?: string;
  emptyMessage?: string;
  selection?: DataTableSelection<T, K>;
  footer?: ReactNode;
  /** Identifies the query behind the rows — filters plus page. See `useScrollReset`. */
  scrollResetKey?: string | number;
}

export function DataTable<T, K extends string | number>({
  caption,
  columns,
  rows,
  rowKey,
  status,
  refreshing = false,
  error,
  onRetry,
  emptyTitle,
  emptyMessage,
  selection,
  footer,
  scrollResetKey,
}: DataTableProps<T, K>) {
  const { t } = useTranslation('admin');
  const scrollRef = useScrollReset<HTMLDivElement>(scrollResetKey);

  if (status === 'loading') {
    // Column-shaped placeholder rather than a centred spinner: the panel keeps
    // the height and column widths it will have once the rows arrive, so the
    // page does not jump when they do.
    return (
      <div className="ui-table-panel" aria-busy="true">
        <div className="ui-table-scroll" role="region" aria-label={caption} tabIndex={0}>
          <table className="ui-table">
            <caption className="sr-only">{caption}</caption>
            <thead>
              <tr>
                {selection ? <th scope="col" className="ui-table__select" /> : null}
                {columns.map(column => (
                  <th
                    key={column.key}
                    scope="col"
                    className={column.numeric ? 'th--numeric' : undefined}>
                    {column.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: SKELETON_ROWS }, (_, rowIndex) => (
                <tr key={rowIndex}>
                  {selection ? <td className="ui-table__select" /> : null}
                  {columns.map(column => (
                    <td key={column.key} className="ui-table__skeleton-cell">
                      <Skeleton />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <span className="sr-only" role="status">
          {t('common.loading')}
        </span>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="ui-table-panel">
        <StateBlock status="error" message={error} onRetry={onRetry} />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="ui-table-panel" aria-busy={refreshing || undefined}>
        <StateBlock status="empty" title={emptyTitle} message={emptyMessage} />
      </div>
    );
  }

  const selectedCount =
    selection == null
      ? 0
      : rows.filter(row => selection.selected.has(rowKey(row))).length;
  const allSelected = selection != null && selectedCount === rows.length;
  const someSelected = selectedCount > 0 && !allSelected;

  return (
    <div className="ui-table-panel" aria-busy={refreshing || undefined}>
      {/* Focusable region: the rows scroll inside this box, and a scrollable box
          that cannot be reached or driven from the keyboard fails WCAG 2.1.1. */}
      <div
        className="ui-table-scroll"
        role="region"
        aria-label={caption}
        tabIndex={0}
        ref={scrollRef}>
        <table className="ui-table">
          <caption className="sr-only">{caption}</caption>
          <thead>
            <tr>
              {selection ? (
                <th scope="col" className="ui-table__select">
                  <input
                    type="checkbox"
                    className="ui-table__checkbox"
                    checked={allSelected}
                    // A partial selection must not look like "nothing selected".
                    ref={node => {
                      if (node) {
                        node.indeterminate = someSelected;
                      }
                    }}
                    onChange={selection.onToggleAll}
                    aria-label={t('common.selectAll')}
                  />
                </th>
              ) : null}
              {columns.map(column => (
                <th
                  key={column.key}
                  scope="col"
                  className={column.numeric ? 'th--numeric' : undefined}>
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(row => {
              const key = rowKey(row);
              const isSelected = selection?.selected.has(key) ?? false;
              return (
                <tr key={key} data-selected={isSelected ? 'true' : undefined}>
                  {selection ? (
                    <td className="ui-table__select">
                      <input
                        type="checkbox"
                        className="ui-table__checkbox"
                        checked={isSelected}
                        onChange={() => selection.onToggle(key)}
                        aria-label={t('common.selectRowNamed', {
                          name: selection.rowLabel(row),
                        })}
                      />
                    </td>
                  ) : null}
                  {columns.map(column => (
                    <td
                      key={column.key}
                      className={column.numeric ? 'td--numeric' : undefined}>
                      {column.render(row)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {footer}
    </div>
  );
}
